// BUSINESS (/business) — OVERVIEW · TRADE · DIGITAL · EVENTS
// 디자인 시안 HTML을 그대로 옮긴 화면입니다. 문구·사진·구성은 시안과 같게 유지해 주세요.
import { useCallback, useEffect, useRef, useState } from "react";
import { usePageMotion } from "../motion/usePageMotion";
import { useHoverIntent } from "../motion/useHoverIntent";

// ONE EXECUTION SYSTEM 4단계 (단계별 TRADE·DIGITAL·EVENTS 실행 내용)
const STEPS = [
  {
    num: "01",
    name: "PLAN",
    kr: "기획·진입 전략",
    desc: "사업 목표부터 실행 조건까지, 태국 시장에 맞게 계획을 구체화합니다.",
    per: [
      ["TRADE", "제품·시장·인허가 기준 검토"],
      ["DIGITAL", "타깃·채널·콘텐츠 설계"],
      ["EVENTS", "행사 목표·공간·프로그램 기획"],
    ],
  },
  {
    num: "02",
    name: "CONNECT",
    kr: "현지 자원 연결",
    desc: "사업에 필요한 바이어·크리에이터·현장 파트너를 연결합니다.",
    per: [
      ["TRADE", "수입사·바이어·유통망 연결"],
      ["DIGITAL", "크리에이터·채널 협업"],
      ["EVENTS", "행사장·참가기업·상담 바이어 연결"],
    ],
  },
  {
    num: "03",
    name: "EXECUTE",
    kr: "실행·현장 운영",
    desc: "준비한 계획을 실제 납품, 콘텐츠, 현장 운영으로 이어갑니다.",
    per: [
      ["TRADE", "통관·납품·판매 운영"],
      ["DIGITAL", "SNS 캠페인·라이브커머스 운영"],
      ["EVENTS", "전시·팝업·수출상담 현장 운영"],
    ],
  },
  {
    num: "04",
    name: "REVIEW",
    kr: "결과·후속 관리",
    desc: "진행 결과를 확인하고 다음 단계에 필요한 자료와 과제를 정리합니다.",
    per: [
      ["TRADE", "유통 운영·후속 거래 점검"],
      ["DIGITAL", "콘텐츠 성과·소비자 반응 분석"],
      ["EVENTS", "운영 실적·상담 내용·결과보고 정리"],
    ],
  },
];
const ANCHORS = [
  ["overview", "OVERVIEW"],
  ["trade", "01 / TRADE"],
  ["digital", "02 / DIGITAL"],
  ["events", "03 / EVENTS"],
];

export default function Business() {
  const mainRef = useRef(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [shown, setShown] = useState(0);
  const [changing, setChanging] = useState(false);
  const activeStep = useRef(0);
  const stepTimer = useRef(null);
  const tabRefs = useRef([]);

  // 탭 전환: 0.15초 뒤 내용 교체
  const choose = useCallback((i) => {
    if (i === activeStep.current) return;
    activeStep.current = i;
    setChanging(true);
    clearTimeout(stepTimer.current);
    stepTimer.current = setTimeout(() => {
      setShown(i);
      setChanging(false);
    }, 150);
  }, []);
  useEffect(() => () => clearTimeout(stepTimer.current), []);
  const tabIntent = useHoverIntent(); // 마우스는 잠깐 머물 때만 단계가 바뀐다(13번)

  // 등장 효과(14번)와 제목 단어별 등장
  const ctaRef = useRef(null);
  usePageMotion(mainRef, {
    split: [".hero-content h1", ".process-heading h2", ".chapter-copy h2"],
    reveal: [
      ".hero-content .eyebrow",
      ".hero-lead",
      ".hero-right-label",
      ".sector-card",
      ".process-heading .minor-label",
      ".process-heading > p",
      ".process-tabs",
      ".chapter-top",
      ".chapter-en",
      ".chapter-copy p",
      ".chapter-photo",
      ".list-heading",
      ".service-grid .service",
      ".case",
      ".chapter-bottom",
    ],
  });
  usePageMotion(ctaRef, { split: [".footer-inner h2"], reveal: [".footer-inner p", ".footer-inner > a"] });

  // 보조 메뉴: 스크롤 위치에 따라 현재 구역 표시
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const sections = [...mainRef.current.querySelectorAll("section[id]")];
    const observer = new IntersectionObserver(
      (es) => {
        for (const e of es) if (e.isIntersecting) setActiveSection(e.target.id);
      },
      { rootMargin: "-18% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const step = STEPS[shown];

  return (
    <>
      <div className="section-nav">
        <div className="container section-nav-inner">
          <span className="page-label">03 / BUSINESS</span>
          <nav className="anchor-nav" aria-label="BUSINESS sections">
            {ANCHORS.map(([id, label]) => (
              <a
                key={id}
                className={activeSection === id ? "active" : undefined}
                aria-current={activeSection === id ? "location" : undefined}
                href={`#${id}`}
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      </div>
      <main ref={mainRef}>
        <section className="overview" id="overview" aria-label="BUSINESS OVERVIEW">
          <div className="container overview-layout">
            <div className="hero">
              <img src="/assets/img/pages/home-hero-1-concept.jpg" alt="방콕 도심과 연결된 태국 시장 진출 비주얼" className="hero-photo" />
              <div className="hero-shade" />
              <div className="hero-content">
                <div className="eyebrow light"><span className="tiny-line" />{" BUSINESS / WHAT WE DO"}</div>
                <h1>시장을 열고,<br />고객과 연결하고,<br /><em>현장에서 실행합니다.</em></h1>
                <p className="hero-lead">시장 진입부터 디지털 커머스, 현장 행사 운영까지.<br />태국 비즈니스의 세 가지 실행 역량을 연결합니다.</p>
              </div>
              <span className="hero-right-label">{"KOREA × THAILAND\u00a0 / \u00a0INBA SINCE 2017"}</span>
            </div>
            <div className="sector-grid" aria-label="BUSINESS AREA">
              <a className="sector-card " href="#trade">
                <div className="sector-top"><span>01 / 수입·인허가·유통</span></div>
                <div className="sector-bottom"><strong>TRADE</strong><p>시장 진입을 실제 판매채널로</p><span>상세보기</span></div>
              </a>
              <a className="sector-card card-featured" href="#digital">
                <div className="sector-top"><span>02 / 디지털 마케팅·커머스</span></div>
                <div className="sector-bottom"><strong>DIGITAL</strong><p>브랜드의 관심을 구매 기회로</p><span>상세보기</span></div>
              </a>
              <a className="sector-card " href="#events">
                <div className="sector-top"><span>03 / 전시·행사·수출상담</span></div>
                <div className="sector-bottom"><strong>EVENTS</strong><p>비즈니스가 만나는 현장으로</p><span>상세보기</span></div>
              </a>
            </div>
            <div className="process" aria-label="ONE EXECUTION SYSTEM">
              <div className="process-heading">
                <div><span className="minor-label">ONE EXECUTION SYSTEM</span><h2>하나의 실행체계, 세 가지 사업.</h2></div>
                <p>사업의 전 과정을 같은 기준으로 관리하고<br />분야별 실행 방식은 목적에 맞게 설계합니다.</p>
              </div>
              <div
                className="process-tabs"
                role="tablist"
                aria-label="사업 공통 프로세스"
                style={{ "--step-progress": (shown / (STEPS.length - 1)).toFixed(4) }}
              >
                {STEPS.map((s, i) => (
                  <button
                    key={s.name}
                    ref={(el) => {
                      tabRefs.current[i] = el;
                    }}
                    className={"process-step" + (i === shown ? " is-active" : "")}
                    data-step={i}
                    role="tab"
                    id={`tab-${i}`}
                    aria-controls="process-details"
                    aria-selected={i === shown ? "true" : "false"}
                    tabIndex={i === shown ? 0 : -1}
                    onMouseEnter={() => tabIntent.enter(() => choose(i))}
                    onMouseLeave={tabIntent.leave}
                    onFocus={() => {
                      tabIntent.leave();
                      choose(i);
                    }}
                    onClick={() => {
                      tabIntent.leave();
                      choose(i);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                        e.preventDefault();
                        const n = (i + (e.key === "ArrowRight" ? 1 : STEPS.length - 1)) % STEPS.length;
                        if (tabRefs.current[n]) tabRefs.current[n].focus();
                      }
                    }}
                  >
                    <span className="step-index">{s.num}</span>
                    <b>{s.name}</b>
                    <small>{s.kr}</small>
                  </button>
                ))}
              </div>
              <div
                className={"process-details" + (changing ? " changing" : "")}
                id="process-details"
                role="tabpanel"
                aria-labelledby={`tab-${shown}`}
                aria-live="polite"
              >
                <div className="step-description">
                  <span className="detail-no">{`${step.num} / ${step.name}`}</span>
                  <h3>{step.kr}</h3>
                  <p>{step.desc}</p>
                </div>
                <div className="domain-uses">
                  {step.per.map(([n, v]) => (
                    <div className="domain-use" key={n}>
                      <b>{n}</b>
                      <span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="chapter chapter-trade" id="trade" aria-label="TRADE business">
          <div className="container chapter-inner">
            <div className="chapter-top">
              <span className="minor-label">BUSINESS / 01 / TRADE</span>
              <a href="#overview" className="back-overview">전체 사업 보기</a>
            </div>
            <div className="chapter-hero">
              <div className="chapter-copy">
                <div className="chapter-en">{"TRADE "}<span>— 수입·인허가·유통</span></div>
                <h2>태국 시장 진입을, 실제 유통으로 이어갑니다.</h2>
                <p>한국 제품의 태국 시장 진출 가능성을 검토하고, 수입·FDA 등 인허가 준비부터 현지 바이어 발굴과 유통채널 연결을 지원합니다. 수입·통관, 현지 납품과 판매 운영까지 사업의 각 단계를 연결합니다.</p>
              </div>
              <div className="chapter-photo">
                <img style={{ objectPosition: "50% 50%" }} src="/assets/img/photos/biz-trade.jpg" alt="INBA를 통해 태국에 진출한 한국 브랜드 제품" loading="eager" />
              </div>
            </div>
            <div className="chapter-body">
              <div className="services">
                <div className="list-heading"><span>WHAT WE DELIVER</span><h3>핵심 서비스</h3></div>
                <div className="service-grid">
                  <div className="service">
                    <span className="service-mini">MARKET RESEARCH</span>
                    <h4>시장·제품 검토</h4>
                    <p>태국 시장과 경쟁제품, 가격·소비자 및 유통환경을 조사해 사업 진입 방향을 검토합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">{"REGULATORY & IMPORT"}</span>
                    <h4>수입·인허가 준비</h4>
                    <p>제품별 FDA 등 현지 인허가 요건을 확인하고 수입 서류와 절차 준비를 지원합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">{"BUYER & CHANNEL"}</span>
                    <h4>바이어·유통채널 연결</h4>
                    <p>태국 현지 바이어와 유통채널을 조사하고 상담, 입점 및 거래 협의 과정을 지원합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">DISTRIBUTION OPERATIONS</span>
                    <h4>통관·물류·판매 운영</h4>
                    <p>통관·물류, 현지 납품 및 판매 운영에 필요한 실무를 사업 범위에 맞춰 연결합니다.</p>
                  </div>
                </div>
              </div>
              <div className="cases">
                <div className="list-heading"><span>SELECTED EXPERIENCE</span><h3>대표 수행 경험</h3></div>
                <div className="case-grid">
                  <article className="case">
                    <div className="case-photo">
                      <img loading="eager" src="/assets/img/pages/business-case-cpall-gluca.jpg" alt="CP ALL × GLUCA 태국 현지 홍보물" />
                    </div>
                    <div className="case-copy">
                      <small>2018–2020 / TRADE</small>
                      <h4>CP ALL × GLUCA Brand Launch</h4>
                      <strong>한국 화장품 브랜드의 태국 편의점 유통시장 진출</strong>
                      <p>한국 화장품 브랜드 GLUCA의 태국 시장 진출을 위해 CP ALL 벤더 등록과 브랜드 런칭을 진행했습니다. 태국 7-Eleven 채널 기반의 제품 납품과 현지 유통을 운영했습니다.</p>
                    </div>
                  </article>
                  <article className="case">
                    <div className="case-photo">
                      <img loading="eager" src="/assets/img/covers/pf-02-poster.jpg" alt="Robinson 백화점 유통 프로젝트 편집 비주얼" style={{ objectPosition: "50% 50%" }} />
                    </div>
                    <div className="case-copy">
                      <small>2019–2021 / TRADE</small>
                      <h4>Robinson Department Store Distribution</h4>
                      <strong>한국 캐릭터 화장품의 태국 백화점 유통 확대</strong>
                      <p>한국 인기 캐릭터 ‘라바’를 활용한 화장품의 Robinson 백화점 벤더 등록을 진행하고, 42개 점포에 제품을 납품하며 현지 백화점 유통을 운영했습니다.</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
            <div className="chapter-bottom">
              <a href="/portfolio?category=trade" data-internal="portfolio" className="portfolio-link">관련 프로젝트 자세히 보기</a>
              <div>
                <span>태국 현지 사업이 필요하신가요?</span>
                <a href="/contact?business=trade#inquiry" data-internal="contact" className="contact-link">사업 문의하기</a>
              </div>
            </div>
          </div>
        </section>
        <section className="chapter chapter-digital" id="digital" aria-label="DIGITAL business">
          <div className="container chapter-inner">
            <div className="chapter-top">
              <span className="minor-label">BUSINESS / 02 / DIGITAL</span>
              <a href="#overview" className="back-overview">전체 사업 보기</a>
            </div>
            <div className="chapter-hero">
              <div className="chapter-copy">
                <div className="chapter-en">{"DIGITAL "}<span>— 디지털 마케팅·커머스</span></div>
                <h2>브랜드를 알리는 것을 넘어, 소비자의 선택으로 이어갑니다.</h2>
                <p>태국 소비자의 관심과 구매 행동을 고려해 SNS 콘텐츠와 캠페인을 기획합니다. 현지 인플루언서, 리뷰·숏폼 콘텐츠, 라이브커머스를 연결해 브랜드 홍보부터 판매 연계까지 실행합니다.</p>
              </div>
              <div className="chapter-photo">
                <img style={{ objectPosition: "50% 45%" }} src="/assets/img/photos/pf-18-g4.jpg" alt="Pet Fair SE Asia 2026 한국관 라이브 방송 현장" loading="eager" />
              </div>
            </div>
            <div className="chapter-body">
              <div className="services">
                <div className="list-heading"><span>WHAT WE DELIVER</span><h3>핵심 서비스</h3></div>
                <div className="service-grid">
                  <div className="service">
                    <span className="service-mini">{"SOCIAL MEDIA & CONTENT"}</span>
                    <h4>SNS 채널·콘텐츠 운영</h4>
                    <p>태국 현지 소비자의 언어와 콘텐츠 소비 방식에 맞춰 SNS 채널과 콘텐츠를 기획·운영합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">{"INFLUENCER & SHORT-FORM"}</span>
                    <h4>인플루언서·숏폼 마케팅</h4>
                    <p>현지 크리에이터와 협업하여 제품 리뷰, 숏폼 영상 및 브랜드 홍보 콘텐츠를 제작합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">LIVE COMMERCE</span>
                    <h4>라이브커머스 기획·운영</h4>
                    <p>라이브 방송 구성과 진행자·인플루언서 협업, 방송 운영 및 판매 프로모션을 지원합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">{"CAMPAIGN & PERFORMANCE"}</span>
                    <h4>디지털 캠페인·성과 분석</h4>
                    <p>온라인 캠페인과 프로모션을 운영하고 콘텐츠 성과 및 소비자 반응을 정리합니다.</p>
                  </div>
                </div>
              </div>
              <div className="cases">
                <div className="list-heading"><span>SELECTED EXPERIENCE</span><h3>대표 수행 경험</h3></div>
                <div className="case-grid">
                  <article className="case">
                    <div className="case-photo">
                      <img loading="eager" src="/assets/img/covers/pf-03-cover.jpg" alt="Enjoy K-Food 운영 채널 화면 편집 이미지" style={{ objectPosition: "50% 50%" }} />
                    </div>
                    <div className="case-copy">
                      <small>2026 / DIGITAL</small>
                      <h4>aT Thailand–Myanmar New Media Marketing</h4>
                      <strong>K-Food의 태국·미얀마 소비자 접점 확대</strong>
                      <p>태국·미얀마 소비자를 위한 K-Food 현지화 SNS 콘텐츠와 온라인 홍보를 운영했습니다. 현지 인플루언서 연계, 프로모션 및 온·오프라인 연계 마케팅을 추진했습니다.</p>
                    </div>
                  </article>
                  <article className="case">
                    <div className="case-photo">
                      <img loading="eager" src="/assets/img/covers/pf-04-cover.jpg" alt="참여 인플루언서 콘텐츠 캡처 편집 이미지" style={{ objectPosition: "50% 50%" }} />
                    </div>
                    <div className="case-copy">
                      <small>2025 / DIGITAL</small>
                      <h4>Korea–Thailand Influencer Live Commerce Pilot</h4>
                      <strong>현지 인플루언서 콘텐츠와 라이브 판매의 결합</strong>
                      <p>한국 브랜드 3개사의 인플루언서 리뷰 콘텐츠와 TikTok 라이브커머스를 연계했습니다. 인플루언서 31명이 참여하고 총 40시간의 라이브 방송을 운영했습니다.</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
            <div className="chapter-bottom">
              <a href="/portfolio?category=digital" data-internal="portfolio" className="portfolio-link">관련 프로젝트 자세히 보기</a>
              <div>
                <span>태국 현지 사업이 필요하신가요?</span>
                <a href="/contact?business=digital#inquiry" data-internal="contact" className="contact-link">사업 문의하기</a>
              </div>
            </div>
          </div>
        </section>
        <section className="chapter chapter-events" id="events" aria-label="EVENTS business">
          <div className="container chapter-inner">
            <div className="chapter-top">
              <span className="minor-label">BUSINESS / 03 / EVENTS</span>
              <a href="#overview" className="back-overview">전체 사업 보기</a>
            </div>
            <div className="chapter-hero">
              <div className="chapter-copy">
                <div className="chapter-en">{"EVENTS "}<span>— 전시·행사·수출상담</span></div>
                <h2>비즈니스가 만나는 현장, 기획부터 운영까지.</h2>
                <p>박람회·팝업스토어·수출상담회를 사업 목표에 맞춰 기획하고, 참가기업과 현지 바이어의 만남을 지원합니다. 현장 협력사·운영 인력 조율부터 프로그램 진행, 운영 자료와 결과 정리까지 수행합니다.</p>
              </div>
              <div className="chapter-photo">
                <img style={{ objectPosition: "50% 55%" }} src="/assets/img/photos/biz-events.jpg" alt="Pet Fair SE Asia 2026 한국관 전경" loading="eager" />
              </div>
            </div>
            <div className="chapter-body">
              <div className="services">
                <div className="list-heading"><span>WHAT WE DELIVER</span><h3>핵심 서비스</h3></div>
                <div className="service-grid">
                  <div className="service">
                    <span className="service-mini">{"EXHIBITION & EVENT PLANNING"}</span>
                    <h4>전시·행사 기획</h4>
                    <p>박람회·팝업·프로모션의 목적과 프로그램을 정하고 공간·동선·일정을 기획합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">{"EXHIBITORS & BUYERS"}</span>
                    <h4>참가기업·바이어 지원</h4>
                    <p>참가기업 안내와 현지 바이어 매칭, 상담 진행 및 통역 등 비즈니스 교류를 지원합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">ON-SITE MANAGEMENT</span>
                    <h4>현장 운영·홍보</h4>
                    <p>현장 협력업체, 운영 인력, 홍보물 및 방문객 프로그램을 조율해 행사를 운영합니다.</p>
                  </div>
                  <div className="service">
                    <span className="service-mini">{"REPORTING & FOLLOW-UP"}</span>
                    <h4>성과관리·결과보고</h4>
                    <p>상담·운영 실적과 현장 증빙자료를 정리하고 결과보고에 필요한 산출물을 지원합니다.</p>
                  </div>
                </div>
              </div>
              <div className="cases">
                <div className="list-heading"><span>SELECTED EXPERIENCE</span><h3>대표 수행 경험</h3></div>
                <div className="case-grid">
                  <article className="case">
                    <div className="case-photo">
                      <img loading="eager" src="/assets/img/photos/biz-case-paradise.jpg" alt="Paradise Park 홍천 특산품 판매전 현장" style={{ objectPosition: "50% 45%" }} />
                    </div>
                    <div className="case-copy">
                      <small>2025 / EVENTS</small>
                      <h4>Paradise Park Hongcheon Special Event</h4>
                      <strong>한국 지역 특산품의 태국 현지 전시·판매·체험</strong>
                      <p>
                        태국 Paradise Park에서 한국 기업 6개사의 제품을 소개하는 전시·판매 행사를 운영했습니다. 제품 판매, 방문객 체험, SNS 인증 캠페인을 결합해 태국 소비자와의 접점을 마련했습니다.
                      </p>
                    </div>
                  </article>
                  <article className="case">
                    <div className="case-photo">
                      <img loading="eager" src="/assets/img/photos/biz-case-seacon.jpg" alt="Seacon Square 행사장" style={{ objectPosition: "50% 50%" }} />
                    </div>
                    <div className="case-copy">
                      <small>2023 / EVENTS</small>
                      <h4>Seacon Square KOREA DAY</h4>
                      <strong>한국 제품과 태국 소비자가 만나는 팝업 행사</strong>
                      <p>태국 Seacon Square에서 한국 기업 4개사와 태국 기업이 참여한 KOREA DAY 팝업을 기획·운영하고, 현지 소비자 대상 전시·홍보 프로그램을 진행했습니다.</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
            <div className="chapter-bottom">
              <a href="/portfolio?category=events" data-internal="portfolio" className="portfolio-link">관련 프로젝트 자세히 보기</a>
              <div>
                <span>태국 현지 사업이 필요하신가요?</span>
                <a href="/contact?business=events#inquiry" data-internal="contact" className="contact-link">사업 문의하기</a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <section ref={ctaRef} className="footer ib-cta" aria-label="사업 문의">
        <div className="container footer-inner">
          <div><h2>{"BUSINESS IN "}<span>MOTION.</span></h2><p>시장을 열고, 고객과 연결하고, 현장에서 실행합니다.</p></div>
          <a href="/contact#inquiry" data-internal="contact">PROJECT INQUIRY</a>
        </div>
      </section>
      <div role="status" aria-live="polite" className="toast" />
    </>
  );
}
